import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { useI18n } from '../../lib/i18n';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  className?: string;
  headerClassName?: string;
}

export interface PaginationInfo {
  totalPages: number;
  currentPage: number;
  start: number;
  end: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export function calculatePagination(
  total: number,
  page: number,
  pageSize: number,
): PaginationInfo {
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(total / safePageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (currentPage - 1) * safePageSize + 1;
  const end = Math.min(currentPage * safePageSize, total);

  return {
    totalPages,
    currentPage,
    start,
    end,
    total,
    hasNext: currentPage < totalPages,
    hasPrev: currentPage > 1,
  };
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor?: (row: T, index: number) => string | number;
  pageSize?: number;
  pageSizes?: number[];
  initialPage?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnKey: string) => void;
  loading?: boolean;
  emptyState?: ReactNode;
  emptyMessage?: string;
  className?: string;
  tableClassName?: string;
}

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  pageSize: propPageSize = 10,
  pageSizes = [5, 10, 25, 50],
  initialPage = 1,
  sortColumn: controlledSortColumn,
  sortDirection: controlledSortDirection,
  onSort: controlledOnSort,
  loading = false,
  emptyState,
  emptyMessage,
  className,
  tableClassName,
}: DataTableProps<T>) {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(propPageSize);

  // Local sorting state if not controlled externally
  const [internalSortColumn, setInternalSortColumn] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>('asc');

  const activeSortColumn = controlledSortColumn !== undefined ? controlledSortColumn : internalSortColumn;
  const activeSortDirection = controlledSortDirection !== undefined ? controlledSortDirection : internalSortDirection;

  const handleSort = (columnKey: string) => {
    if (controlledOnSort) {
      controlledOnSort(columnKey);
    } else {
      if (internalSortColumn === columnKey) {
        setInternalSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setInternalSortColumn(columnKey);
        setInternalSortDirection('asc');
      }
    }
  };

  // Sorted data (if uncontrolled)
  const sortedData = useMemo(() => {
    if (controlledSortColumn !== undefined || !activeSortColumn) return data;
    return [...data].sort((a: any, b: any) => {
      const valA = a[activeSortColumn];
      const valB = b[activeSortColumn];
      if (valA === valB) return 0;
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;
      const comparison = valA < valB ? -1 : 1;
      return activeSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, activeSortColumn, activeSortDirection, controlledSortColumn]);

  const pagination = useMemo(
    () => calculatePagination(sortedData.length, currentPage, pageSize),
    [sortedData.length, currentPage, pageSize],
  );

  const paginatedData = useMemo(() => {
    if (pagination.total === 0) return [];
    const startIndex = (pagination.currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, pagination.currentPage, pageSize]);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-line bg-white shadow-card transition-colors',
        'dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className={cn('w-full border-collapse text-start', tableClassName)}>
          <thead>
            <tr className="border-b border-line bg-slate-50/70 dark:border-slate-800 dark:bg-slate-800/50">
              {columns.map((col) => {
                const isSorted = activeSortColumn === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    className={cn(
                      'px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400 select-none',
                      col.align === 'center' && 'text-center',
                      col.align === 'end' && 'text-end',
                      (!col.align || col.align === 'start') && 'text-start',
                      col.sortable && 'cursor-pointer hover:text-ink dark:hover:text-slate-200',
                      col.headerClassName,
                    )}
                  >
                    <div
                      className={cn(
                        'inline-flex items-center gap-1.5',
                        col.align === 'center' && 'justify-center',
                        col.align === 'end' && 'justify-end',
                      )}
                    >
                      <span>{col.header}</span>
                      {col.sortable && (
                        <span className="text-xs text-ink-4 dark:text-slate-500">
                          {isSorted ? (activeSortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-line dark:divide-slate-800 text-[13px]">
            {loading ? (
              Array.from({ length: 5 }).map((_, rIdx) => (
                <tr key={`skeleton-${rIdx}`} className="border-b border-line/60 dark:border-slate-800/60 animate-pulse">
                  {columns.map((col, cIdx) => (
                    <td key={col.key || cIdx} className="px-4 py-3.5">
                      <div
                        className={cn(
                          'h-4 rounded bg-slate-200/80 dark:bg-slate-800/80',
                          cIdx === 0 ? 'w-3/4' : cIdx === columns.length - 1 ? 'w-12 ms-auto' : 'w-1/2',
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-ink-3 dark:text-slate-400">
                  {emptyState || emptyMessage || t('ui.noData') || 'No records found.'}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIdx) => {
                const rowKey = keyExtractor ? keyExtractor(row, rowIdx) : (row as any)?.id ?? rowIdx;
                return (
                  <tr
                    key={rowKey}
                    className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 py-3 text-ink-2 dark:text-slate-300 align-middle',
                          col.align === 'center' && 'text-center',
                          col.align === 'end' && 'text-end',
                          (!col.align || col.align === 'start') && 'text-start',
                          col.className,
                        )}
                      >
                        {col.render ? col.render(row, rowIdx) : (row as any)?.[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3 text-xs text-ink-3 dark:border-slate-800 dark:text-slate-400">
        <div className="flex items-center gap-2">
          <span>{t('ui.rowsPerPage') || 'Rows per page:'}</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span className="font-mono text-xs font-medium">
            {pagination.start} – {pagination.end} {t('ui.of') || 'of'} {pagination.total}
          </span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={!pagination.hasPrev}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              aria-label={t('ui.prevPage') || 'Previous page'}
              className="grid h-7 w-7 place-items-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:pointer-events-none dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <ChevronLeftIcon size={14} className="rtl:rotate-180" />
            </button>
            <button
              type="button"
              disabled={!pagination.hasNext}
              onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
              aria-label={t('ui.nextPage') || 'Next page'}
              className="grid h-7 w-7 place-items-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:pointer-events-none dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <ChevronRightIcon size={14} className="rtl:rotate-180" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
